/**
 * TypeScript port of the switch ACL Jinja2 templates.
 *
 * Mirrors:
 *   templates/switch/aruba/sections/acl.j2
 *   templates/switch/hp/sections/acl.j2
 *   templates/switch/ruckus/sections/acl.j2
 */

function solveIpEquation(equation: string, baseIp: string): string {
  if (!baseIp || !baseIp.includes(".")) return equation;
  const [w, x, y, z] = baseIp.split(".").map(Number);
  const vars: Record<string, number> = { w, x, y, z };
  return equation
    .split(".")
    .map((part) => {
      if (/^\d+$/.test(part)) return part;
      const m = part.match(/^([wxyz])([+-]\d+)?$/);
      if (!m) return part;
      const base = vars[m[1]] ?? 0;
      const delta = m[2] ? parseInt(m[2], 10) : 0;
      return String(Math.min(255, Math.max(0, base + delta)));
    })
    .join(".");
}

// ─── Aruba CX ─────────────────────────────────────────────────────────────────

function renderArubaAcl(acl_type: string): string {
  let body = "";

  if (acl_type === "gre_mvp") {
    body = `access-list ip ACL_PortIso
  permit any 192.168.0.0/16 any
  comment 'Allow traffic to OVI from Guest Networks'
  permit any 172.20.0.0/16 172.20.0.1
  permit any 172.20.0.0/16 172.20.24.1
  permit any 172.20.0.0/16 172.20.48.1
  comment 'Allow Associate Free networks'
  permit any 172.20.0.0/16 172.20.32.1
  comment 'Allow DHCP only from valid sources'
  permit udp any eq dhcp-client 255.255.255.255 eq dhcp-server
  deny udp any eq dhcp-server any eq dhcp-client
  comment 'Allow GRE P2 STB traffic to Gateway and Pro:Centric'
  permit any 172.19.0.0/16 172.19.32.1
  permit any 172.19.0.0/16 172.19.32.2
  permit any 172.19.0.0/16 172.19.32.6
  comment 'Allow IGMP to STB and Chromecast'
  permit igmp 172.19.0.0/16 224.0.0.0/240.0.0.0
  permit igmp 172.17.52.0/255.255.252.0 224.0.0.0/240.0.0.0
  comment 'Deny inter-user traffic'
  deny any 172.20.0.0/16 172.20.0.0/16
  comment 'Deny inter-STB traffic'
  deny any 172.19.0.0/16 172.19.0.0/16
  comment 'Deny discovery protocols'
  deny udp 172.20.0.0/16 0.0.0.0 eq 1900
  deny udp 172.20.0.0/16 0.0.0.0 eq 2869
  deny tcp 172.20.0.0/16 0.0.0.0 eq 1900
  comment 'Permit all'
  permit any any any
exit`;
  } else if (acl_type === "gre2") {
    body = `access-list ip ACL_PortIso
  comment  'Allow traffic to OVI from Guest Networks'
  permit ip 172.20.0.0/16 172.20.0.1/32
  permit ip 172.20.0.0/16 172.20.24.1/32
  permit ip 172.20.0.0/16 172.20.48.1/32
  comment  'Allow Associate Free networks'
  permit ip 172.20.0.0/16 172.20.32.1/32
  comment  'Allow DHCP only from valid sources'
  permit udp 0.0.0.0/0 eq 68 255.255.255.255/32 eq 67
  deny udp 0.0.0.0/0 eq 67 0.0.0.0/0 eq 68
  comment  'Allow GRE P2 STB traffic to Gateway and Pro:Centric'
  permit ip 172.19.0.0/16 172.19.32.1/32
  permit ip 172.19.0.0/16 172.19.32.2/32
  permit ip 172.19.0.0/16 172.19.32.6/32
  comment  'Allow IGMP to STB and Chromecast'
  permit igmp 172.19.0.0/16 224.0.0.0/4
  permit igmp 172.17.52.0/22 224.0.0.0/4
  comment  'Deny inter-user traffic'
  deny ip 172.20.0.0/16 172.20.0.0/16
  comment  'Deny inter-STB traffic'
  deny ip 172.19.0.0/16 172.19.0.0/16
  comment  'Deny discovery protocols'
  deny udp 172.20.0.0/16 0.0.0.0/32 eq 1900
  deny udp 172.20.0.0/16 0.0.0.0/32 eq 2869
  deny tcp 172.20.0.0/16 0.0.0.0/32 eq 1900
  comment  'Permit all'
  permit any any any
exit`;
  } else if (acl_type === "hsia") {
    body = `no access-list ip ACL_PortIso
access-list ip ACL_PortIso
\tpermit ip 172.20.0.0/16 172.20.0.1/32
\tpermit ip 172.20.0.0/16 172.20.16.1/32
\tpermit ip 172.20.0.0/16 172.20.24.1/32
\tpermit ip 172.20.0.0/16 172.20.48.1/32
\tpermit udp any eq 68 255.255.255.255/32 eq 67
\tdeny udp any eq 67 any eq 68
\tpermit ip 172.20.0.0/16 224.0.0.251/32
\tdeny ip 172.20.0.0/16 172.16.0.0/12
\tdeny ip 172.20.0.0/16 192.168.0.0/16
\tdeny ip 172.20.0.0/16 10.0.0.0/8
\tdeny ip 172.20.0.0/16 255.255.255.255/32
\tdeny ip 172.20.0.0/16 224.0.0.0/4
\tpermit ip any any
\texit`;
  }

  return `${body}
no access-list ipv6 ACL_IPv6_PortIso
access-list ipv6 ACL_IPv6_PortIso
  deny ipv6 any any
  exit`;
}

// ─── HP / Aruba AOS ──────────────────────────────────────────────────────────

function renderHpAcl(acl_type: string): string {
  let body = "";

  if (acl_type === "gre_mvp") {
    body = `no ip access-list extended "ACL_PortIso"
ip access-list extended "ACL_PortIso"
  10 permit ip 172.20.0.0 0.0.255.255 172.20.0.1 0.0.0.0
  20 permit ip 172.20.0.0 0.0.255.255 172.20.0.2 0.0.0.0
  30 permit ip 172.20.0.0 0.0.255.255 172.20.16.1 0.0.0.0
  40 permit ip 172.20.0.0 0.0.255.255 172.20.24.1 0.0.0.0
  50 permit ip 172.20.0.0 0.0.255.255 172.20.48.1 0.0.0.0
  60 permit ip 172.31.240.0 0.0.3.255 172.31.240.1 0.0.0.0
  70 permit udp 0.0.0.0 255.255.255.255 eq 68 255.255.255.255 0.0.0.0 eq 67
  80 deny udp 0.0.0.0 255.255.255.255 eq 67 0.0.0.0 255.255.255.255 eq 68
  90 permit ip 172.20.0.0 0.0.255.255 224.0.0.251 0.0.0.0
  100 permit ip 172.31.240.0 0.0.3.255 224.0.0.251 0.0.0.0
  110 permit ip 172.19.0.0 0.0.255.255 172.19.255.0 0.0.0.255
  120 permit ip 172.19.0.0 0.0.255.255 172.30.254.1 0.0.0.0
  130 permit igmp 172.19.0.0 0.0.255.255 0.0.0.0 255.255.255.255
  140 deny ip 172.20.0.0 0.0.255.255 172.16.0.0 0.15.255.255
  150 deny ip 172.20.0.0 0.0.255.255 192.168.0.0 0.0.255.255
  160 deny ip 172.20.0.0 0.0.255.255 10.0.0.0 0.255.255.255
  170 deny ip 172.20.0.0 0.0.255.255 255.255.255.255 0.0.0.0
  180 deny ip 172.20.0.0 0.0.255.255 224.0.0.0 15.255.255.255
  190 deny ip 172.31.240.0 0.0.3.255 172.16.0.0 0.15.255.255
  200 deny ip 172.31.240.0 0.0.3.255 192.168.0.0 0.0.255.255
  210 deny ip 172.31.240.0 0.0.3.255 10.0.0.0 0.255.255.255
  220 deny ip 172.31.240.0 0.0.3.255 255.255.255.255 0.0.0.0
  230 deny ip 172.31.240.0 0.0.3.255 224.0.0.0 15.255.255.255
  240 deny ip 172.19.0.0 0.0.255.255 172.16.0.0 0.15.255.255
  250 deny ip 172.19.0.0 0.0.255.255 192.168.0.0 0.0.255.255
  260 deny ip 172.19.0.0 0.0.255.255 10.0.0.0 0.255.255.255
  270 deny ip 172.19.0.0 0.0.255.255 255.255.255.255 0.0.0.0
  280 deny ip 172.19.0.0 0.0.255.255 224.0.0.0 15.255.255.255
  290 deny udp 172.19.0.0 0.0.255.255 0.0.0.0 255.255.255.255 eq 1900
  300 deny udp 172.19.0.0 0.0.255.255 0.0.0.0 255.255.255.255 eq 2869
  310 deny tcp 172.19.0.0 0.0.255.255 0.0.0.0 255.255.255.255 eq 2869
  320 permit ip 0.0.0.0 255.255.255.255 0.0.0.0 255.255.255.255
  exit`;
  } else if (acl_type === "gre2") {
    body = `no ip access-list extended "ACL_PortIso"
ip access-list extended "ACL_PortIso"
10 remark "Allow traffic to OVI from Guest Networks"
20 permit ip 172.20.0.0 0.0.255.255 172.20.0.1 0.0.0.0
30 permit ip 172.20.0.0 0.0.255.255 172.20.24.1 0.0.0.0
50 permit ip 172.20.0.0 0.0.255.255 172.20.48.1 0.0.0.0
60 remark "Allow Associate Free networks"
70 permit ip 172.20.0.0 0.0.255.255 172.20.32.1 0.0.0.0
80 remark "Allow DHCP only from valid sources"
90 permit udp 0.0.0.0 255.255.255.255 eq 68 255.255.255.255 0.0.0.0 eq 67
100 deny udp 0.0.0.0 255.255.255.255 eq 67 0.0.0.0 255.255.255.255 eq 68
110 remark "Allow GRE P2 STB traffic to Gateway and Pro:Centric"
120 permit ip 172.19.0.0 0.0.255.255 172.19.32.1 0.0.0.0
130 permit ip 172.19.0.0 0.0.255.255 172.19.32.2 0.0.0.0
140 remark "Allow IGMP to STB and Chromecast"
150 permit igmp 172.19.0.0 0.0.255.255 224.0.0.0 15.255.255.255
160 permit igmp 172.17.52.0 0.0.3.255 224.0.0.0 15.255.255.255
170 remark "Deny inter-user traffic"
180 deny ip 172.20.0.0 0.0.255.255 172.20.0.0 0.0.255.255
190 remark "Deny inter-STB traffic"
200 deny ip 172.19.0.0 0.0.255.255 172.19.0.0 0.0.255.255
210 remark "Deny discovery protocols"
220 deny udp 172.20.0.0 0.0.255.255 0.0.0.0 255.255.255.255 eq 1900
230 deny udp 172.20.0.0 0.0.255.255 0.0.0.0 255.255.255.255 eq 2869
240 deny tcp 172.20.0.0 0.0.255.255 0.0.0.0 255.255.255.255 eq 1900
250 remark "Permit all"
260 permit ip 0.0.0.0 255.255.255.255 0.0.0.0 255.255.255.255
exit`;
  } else if (acl_type === "hsia") {
    body = `no ip access-list extended "ACL_PortIso"
ip access-list extended "ACL_PortIso"
10 permit ip 172.20.0.0 0.0.255.255 172.20.0.1 0.0.0.0
20 permit ip 172.20.0.0 0.0.255.255 172.20.16.1 0.0.0.0
30 permit ip 172.20.0.0 0.0.255.255 172.20.24.1 0.0.0.0
40 permit ip 172.20.0.0 0.0.255.255 172.20.48.1 0.0.0.0
50 permit udp 0.0.0.0 255.255.255.255 eq 68 255.255.255.255 0.0.0.0 eq 67
60 deny udp 0.0.0.0 255.255.255.255 eq 67 0.0.0.0 255.255.255.255 eq 68
70 permit ip 172.20.0.0 0.0.255.255 224.0.0.251 0.0.0.0
80 deny ip 172.20.0.0 0.0.255.255 172.16.0.0 0.15.255.255
90 deny ip 172.20.0.0 0.0.255.255 192.168.0.0 0.0.255.255
100 deny ip 172.20.0.0 0.0.255.255 10.0.0.0 0.255.255.255
110 deny ip 172.20.0.0 0.0.255.255 255.255.255.255 0.0.0.0
120 deny ip 172.20.0.0 0.0.255.255 224.0.0.0 15.255.255.255
130 permit ip 0.0.0.0 255.255.255.255 0.0.0.0 255.255.255.255
exit`;
  }

  return `${body}
no access-list ipv6 ACL_IPv6_PortIso
ipv6 access-list "ACL_IPv6_PortIso"
exit`;
}

// ─── Ruckus (Brocade) ─────────────────────────────────────────────────────────

function renderRuckusAcl(acl_type: string, switch_ip: string): string {
  const OVI_MANAGEMENT_IP_22 = solveIpEquation("w.x.y.129", switch_ip);
  const OVI_MANAGEMENT_IP_21 = solveIpEquation("w.x.y.1", switch_ip);

  const header = `ipv6 access-list ACL_IPv6_PortIso
 sequence 10 deny ipv6 any any
!
ip access-list standard 1
 sequence 10 permit host ${OVI_MANAGEMENT_IP_22}
 ! (alternative /21 mask): sequence 10 permit host ${OVI_MANAGEMENT_IP_21}
!`;

  let body: string;

  if (acl_type === "hsia") {
    body = `ip access-list extended ACL_PortIso
 permit ip host 172.20.40.1 172.20.0.0 0.0.255.255
 permit ip host 172.20.0.1 172.20.0.0 0.0.255.255
 permit ip host 172.20.16.1 172.20.0.0 0.0.255.255
 permit ip host 172.20.24.1 172.20.0.0 0.0.255.255
 permit ip host 172.20.48.1 172.20.0.0 0.0.255.255
 permit ip host 172.31.240.1 172.31.240.0 0.0.3.255
 permit ip 172.20.0.0 0.0.255.255 host 172.20.0.1
 permit ip 172.20.0.0 0.0.255.255 host 172.20.16.1`;
  } else {
    body = `ip access-list extended ACL_Video_PortIso
 sequence 10 permit ip 172.19.0.6 0.0.31.0 172.19.0.0 0.0.255.255
 sequence 20 permit ip 172.19.32.6 0.0.15.0 172.19.0.0 0.0.255.255
 sequence 30 permit ip 172.19.254.0 0.0.1.255 172.19.0.0 0.0.255.255
 sequence 40 permit ip 172.19.254.0 0.0.1.255 224.0.0.0 15.255.255.255
 sequence 50 permit ip 172.19.0.0 0.0.255.255 172.19.255.0 0.0.0.255
 sequence 60 permit ip 172.19.0.0 0.0.255.255 172.19.0.6 0.0.31.0
 sequence 70 permit ip 172.19.0.0 0.0.255.255 172.19.32.6 0.0.15.0
 sequence 80 permit udp any eq bootpc host 255.255.255.255 eq bootps
 sequence 90 permit udp host 172.30.254.1 eq bootps any eq bootpc
 sequence 100 deny udp any eq bootps any eq bootpc
 sequence 110 permit ip 172.19.0.0 0.0.255.255 host 172.30.254.1
 sequence 120 permit igmp 172.19.0.0 0.0.255.255 any
 sequence 130 deny ip 172.19.0.0 0.0.255.255 172.16.0.0 0.15.255.255
 sequence 140 deny ip 172.19.0.0 0.0.255.255 192.168.0.0 0.0.255.255
 sequence 150 deny ip 172.19.0.0 0.0.255.255 10.0.0.0 0.255.255.255
 sequence 160 deny ip 172.19.0.0 0.0.255.255 host 255.255.255.255
 sequence 170 deny ip 172.19.0.0 0.0.255.255 224.0.0.0 15.255.255.255
 sequence 180 deny udp 172.19.0.0 0.0.255.255 any eq 1900
 sequence 190 deny udp 172.19.0.0 0.0.255.255 any eq 2869
 sequence 200 deny tcp 172.19.0.0 0.0.255.255 any eq 2869
 sequence 210 permit ip any any
!
!
!`;
  }

  return `${header}\n${body}`;
}

// ─── Entry point ──────────────────────────────────────────────────────────────

export function renderSwitchAcl(vendor: string, acl_type: string, switch_ip: string): string {
  if (vendor === "aruba") return renderArubaAcl(acl_type);
  if (vendor === "hp")    return renderHpAcl(acl_type);
  if (vendor === "ruckus") return renderRuckusAcl(acl_type, switch_ip);
  return "";
}
